<template>
  <div :class="{'date-picker-wrapper': true, 'with-time': showTime, 'without-time': !showTime,  }">
    <div class="date-picker-menu">
      <v-menu
        v-model="menuVisible"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        max-width="290px"
        v-bind="componentAttrs.menu"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="displayValue"
            prepend-icon="event"
            placeholder="YYYY-MM-DD"
            hide-details
            :rules="dateRules"
            v-bind="componentAttrs.textField "
            v-on="on"
            class="date-picker__input"
            @input="handleInput"
            @paste.stop.prevent="handlePaste"
          />
        </template>

        <v-date-picker
          :value="validDate"
          :picker-date="pickerDate"
          no-title
          scrollable
          v-bind="componentAttrs.datePicker"
          @input="handleDateChange"
        >
          <slot name="date-picker-content">
            <template v-if="showTime">
              <v-text-field
                :value="timeValue"
                label="Time"
                class="col-4"
                type="time"
                step="2"
                placeholder="HH:mm:ss"
                hide-details
                :rules="timeRules"
                v-bind="componentAttrs.timeField"
                @input="handleTimeChange"
              />
              <v-spacer />
              <v-btn
                text
                color="primary"
                @click="closeMenu"
              >
                OK
              </v-btn>
            </template>
          </slot>
        </v-date-picker>
      </v-menu>

      <v-text-field
        v-if="showTime"
        :value="timeValue"
        label="Time"
        class="date-picker__time-input"
        type="time"
        step="2"
        placeholder="HH:mm:ss"
        hide-details
        :rules="timeRules"
        v-bind="componentAttrs.timeField"
        @input="handleTimeChange"
      />
    </div>

    <v-messages
      v-if="hint"
      :value="[hint]"
      class="date-picker__hint"
      color="grey darken-1"
    />
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'DatePicker',
  inheritAttrs: false,
  props: {
    value: {
      type: String,
      default: null
    },
    showTime: {
      type: Boolean,
      default: false
    },
    timeValue: {
      type: String,
      default: null
    },
    checkValidation: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      menuVisible: false
    }
  },
  computed: {
    componentAttrs () {
      const {
        label,
        readonly,
        disabled,
        ...restAttrs
      } = this.$attrs

      const resolvedLabel = label || 'Date'
      const resolvedReadonly = readonly != null && readonly !== false
      const resolvedDisabled = disabled != null && disabled !== false

      return {
        menu: {
          disabled: resolvedDisabled || resolvedReadonly,
        },
        textField: {
          label: resolvedLabel,
          readonly: resolvedReadonly,
          disabled: resolvedDisabled,
          ...restAttrs
        },
        datePicker: {
          readonly: resolvedReadonly,
          disabled: resolvedDisabled
        },
        timeField: {
          readonly: resolvedReadonly,
          disabled: resolvedDisabled
        }
      }
    },
    displayValue () {
      return this.value
    },
    validDate () {
      const parsed = this.parseDate(this.value)
      return parsed.valid
    },
    pickerDate () {
      const parsed = this.parseDate(this.value)
      return parsed.picker
    },
    dateRules () {
      if (!this.checkValidation) {
        return []
      }
      return [
        (v) => {
          if (!v) return 'Date is required'
          const m = moment(v, 'YYYY-MM-DD', true)
          return m.isValid() || 'Invalid date format'
        }
      ]
    },
    timeRules () {
      if (!this.checkValidation || !this.showTime) {
        return []
      }
      return [
        (v) => {
          if (!v) return 'Time is required'
          const m = moment(v, 'HH:mm:ss', true)
          return m.isValid() || 'Invalid time format'
        }
      ]
    }
  },
  methods: {
    parseDate (dateString) {
      if (!dateString) {
        return { valid: null, picker: null }
      }

      let m = moment(dateString, 'YYYY-MM-DD', true)
      if (m.isValid()) {
        const formatted = m.format('YYYY-MM-DD')
        return {
          valid: formatted,
          picker: null
        }
      }

      m = moment(dateString, 'YYYY-MM', true)
      if (m.isValid()) {
        return {
          valid: null,
          picker: m.startOf('month').format('YYYY-MM-DD')
        }
      }

      m = moment(dateString, 'YYYY', true)
      if (m.isValid()) {
        return {
          valid: null,
          picker: m.startOf('year').format('YYYY-MM-DD')
        }
      }

      return { valid: null, picker: null }
    },
    handleInput (value) {
      this.$emit('input', value)
    },
    handleDateChange (date) {
      this.$emit('input', date)
      if ( !this.showTime) {
        this.closeMenu()
      }
    },
    handleTimeChange (time) {
      this.$emit('time-change', time)
    },
    handlePaste (event) {
      if (!this.showTime) {
        this.$emit('paste', event)
        return
      }

      const pastedText = (event.clipboardData || {}).getData('text/plain')
      let pasteDate = moment(pastedText)

      if (!pasteDate.isValid()) {
        pasteDate = moment(this.value + ' ' + (this.timeValue || '00:00:00'))
      }

      if (!pasteDate.isValid()) {
        this.$emit('paste', event)
        return
      }

      const parts = pasteDate.format('YYYY-MM-DD HH:mm:ss').split(' ')

      this.$emit('input', parts[0])
      this.$emit('time-change', parts[1])
    },
    closeMenu () {
      this.menuVisible = false
      if (this.showTime) {
        this.$emit('close')
      }
    }
  },
  watch: {
    menuVisible (newVal) {
      if (!newVal) {
        this.$emit('close')
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/table/report-table.scss';

  .date-picker-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  .date-picker-wrapper.with-time {
    min-width: 260px;
  }

  .date-picker-wrapper.without-time {
    min-width: 160px;
  }

  .date-picker-menu {
    display: flex;
    align-items: flex-start;
    gap: 0;
    flex-wrap: nowrap;
  }

  .date-picker__input {
    padding: 12px 0px 4px 8px;
    flex: 1 1 auto;
    min-width: 110px;

    ::v-deep .v-input__control {
      .v-input__slot {
        padding-right: 12px !important;
      }
    }
  }

  .date-picker__time-input {
    flex: 0 0 auto;
  }

  .date-picker__hint {
    flex: 0 0 100%;
    padding-left: 41px;
    font-size: 12px;
  }

</style>
